import React, { useState, useMemo, useCallback } from 'react'
import styled from '@emotion/styled'
import { Typography, Spin, Button, Modal } from 'antd'
import { useRecoilValue } from 'recoil'
import { useNavigate } from 'react-router-dom'

import SVGLoader from '@src/components/svg/SVGLoader'
import vendorAtom from '@src/atom/vendor'
import { CardDto, GetCardQueryKey } from '@src/type/payment'
import { useGetCardQuery } from '@src/hooks/queries/payment/useGetCardQuery'

const { Title, Text, Link } = Typography

export const PlanPage: React.FC = () => {
	const navigate = useNavigate()

	const vendor = useRecoilValue(vendorAtom)

	const vendorNo = useMemo(() => {
		const parsedVendorNo = Number.parseInt(vendor.no, 10)
		const no = Number.isNaN(parsedVendorNo) ? undefined : parsedVendorNo

		return no
	}, [vendor])

	const queryKey = useMemo<GetCardQueryKey>(() => ['getCard', vendorNo], [vendorNo])

	const { data: cardResBody } = useGetCardQuery({ queryKey })

	const card = useMemo<CardDto | null | undefined>(() => {
		if (!cardResBody) {
			return undefined
		}

		if (cardResBody.data.card_info_list.length <= 0) {
			return null
		}

		return cardResBody.data.card_info_list[0]
	}, [cardResBody])

	const onClickActionButton = useCallback(() => {
		navigate('/payment/subscribe/info')
	}, [navigate])

	const [isContactModalOpened, setIsContactModalOpened] = useState(false)

	const onClickContactButton = useCallback(() => {
		setIsContactModalOpened(true)
	}, [])

	if (card === undefined) {
		return <Spin />
	}

	return (
		<>
			<Container>
				<Title level={5}>
					{card
						? '스탠다드 요금제를 사용하고 있습니다'
						: '서비스를 이용하려면 카드 등록을 진행해 주세요'}
				</Title>
				<Plans>
					<PlanCardContainer selected={!!card}>
						<PlanCardHeader>
							<PlanName>스탠다드</PlanName>
							<PlanDescription>
								챌린지를 통해 브랜드 인지도를 향상을 하거나 자체 판매채널을 활용하여 마케팅하고
								싶을때
							</PlanDescription>
							<PlanPriceWrapper>
								<PlanOriginalPrice>500,000</PlanOriginalPrice>
								<PlanMonthPriceWrapper>
									<PlanMonthPrice>99,000원</PlanMonthPrice>
									<PlanMonth>/월</PlanMonth>
								</PlanMonthPriceWrapper>
							</PlanPriceWrapper>
						</PlanCardHeader>
						<PlanCardDivider />
						<PlanFeatures>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>브랜드 페이지 생성</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>모티너스 메인 페이지 노출</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>챌린지 참여자 수 ~1000명</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>핫딜 챌린지 생성 수 2회</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>핫딜 챌린지 시작 알림톡 발송 1회</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>구매 유도 알림톡 발송 1회</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>핫딜 챌린지 홍보 노티 발송 1회</PlanFeatureDescription>
							</PlanFeatureWrapper>
						</PlanFeatures>
						<PlanCardActionButton
							status={card ? 'inactive' : 'active'}
							onClick={card ? undefined : onClickActionButton}
						>
							{card ? '지금 쓰는 요금제' : '카드 등록 후 시작하기'}
						</PlanCardActionButton>
					</PlanCardContainer>

					{/* Space */}

					<PlanCardContainer>
						<PlanCardHeader>
							<PlanName>프로</PlanName>
							<PlanDescription>
								온라인 상담을 받으며, 모티너스 전체 서비스를 이용해, 직접 성과를 내고 싶을때
							</PlanDescription>
							<PlanPriceWrapper>
								<PlanOriginalPrice>1,000,000</PlanOriginalPrice>
								<PlanMonthPriceWrapper>
									<PlanMonthPrice>499,000원</PlanMonthPrice>
									<PlanMonth>/월</PlanMonth>
								</PlanMonthPriceWrapper>
							</PlanPriceWrapper>
						</PlanCardHeader>
						<PlanCardDivider />
						<PlanFeatures>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>스탠다드 모든 기능</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>1:1 온라인 상담</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>모티샵 입점 판매/수수료 2%</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>제품 인증 사진 무한 다운로드</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>챌린지 참여자 수 무제한</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>핫딜 챌린지 생성 수 무제한</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>모티벨 퀴즈(CRM) 생성 수 무제한</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>챌린지 별 참여자/구매자 통계</PlanFeatureDescription>
							</PlanFeatureWrapper>
						</PlanFeatures>
						<PlanCardActionButton status="normal" onClick={onClickContactButton}>
							영업팀에 문의하기
						</PlanCardActionButton>
					</PlanCardContainer>

					{/* Space */}

					<PlanCardContainer>
						<PlanCardHeader>
							<PlanName>엔터프라이즈</PlanName>
							<PlanDescription>맞춤 기능과 서비스/CS 담당 인원이 필요할때</PlanDescription>
							<PlanPriceWrapper>
								<PlanOriginalPrice />
								<PlanMonthPriceWrapper>
									<PlanMonthPrice>문의하기</PlanMonthPrice>
									<PlanMonth>/월</PlanMonth>
								</PlanMonthPriceWrapper>
							</PlanPriceWrapper>
						</PlanCardHeader>
						<PlanCardDivider />
						<PlanFeatures>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>프로 모든 기능</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>프로젝트 담당 디렉터 배정</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>체험단 마케팅 서비스</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>블로그 체험단 서비스</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>리뷰 체험단 서비스</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>SNS 체험단 서비스</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>CS 대응</PlanFeatureDescription>
							</PlanFeatureWrapper>
							<PlanFeatureWrapper>
								<CheckIconWrapper>
									<SVGLoader name="checkCircle" />
								</CheckIconWrapper>
								<PlanFeatureDescription>시스템 연동 지원</PlanFeatureDescription>
							</PlanFeatureWrapper>
						</PlanFeatures>
						<PlanCardActionButton status="normal" onClick={onClickContactButton}>
							영업팀에 문의하기
						</PlanCardActionButton>
					</PlanCardContainer>
				</Plans>
				<Footer>
					<Text style={{ color: '#4E4E4E' }}>
						{card ? '구독 해지는 채널톡으로 1:1 문의해 주세요' : ''}
					</Text>
					<Link
						href="https://picturesque-avatar-ac9.notion.site/5b4474ae70b34ee5a521dcf8ffa68ba1?pvs=4"
						target="_blank"
					>
						환불정책
					</Link>
				</Footer>
			</Container>
			<Modal
				open={isContactModalOpened}
				centered
				closable={false}
				title={<ContactModalHeader />}
				footer={<ContactModalFooter setIsContactModalOpened={setIsContactModalOpened} />}
				onCancel={() => setIsContactModalOpened(false)}
			>
				<ContactModalBody />
			</Modal>
		</>
	)
}

const contactModalHeader: React.FC = () => (
	<ModalHeaderWrapper>
		<ModalHeaderTitle>1:1 문의는 아래 번호로 연락주세요.</ModalHeaderTitle>
		<ModalHeaderSubTitle>(평일 10:00~18:00 까지)</ModalHeaderSubTitle>
	</ModalHeaderWrapper>
)

const ContactModalHeader = React.memo(contactModalHeader)

const contactModalBody: React.FC = () => (
	<ModalBodyWrapper>
		<ModalBodyContact>📞 : 010-9768-2105</ModalBodyContact>
		<ModalBodyUrgentContact>📞 : (긴급 전화) 010-3454-4430</ModalBodyUrgentContact>
	</ModalBodyWrapper>
)

const ContactModalBody = React.memo(contactModalBody)

const contactModalFooter: React.FC<{
	setIsContactModalOpened: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ setIsContactModalOpened }) => (
	<Button type="primary" onClick={() => setIsContactModalOpened(false)}>
		확인
	</Button>
)

const ContactModalFooter = React.memo(contactModalFooter)

const Container = styled.div`
	margin-bottom: 80px;
`

const Plans = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	gap: 20px;
	align-items: flex-start;
	margin-top: 16px;
`

const PlanCardContainer = styled.div<{ selected?: boolean }>`
	border: 1px solid ${({ selected }) => (selected ? '#FFB257' : '#e0d9d9')};
	background: ${({ selected }) => (selected ? '#FFFCF2' : '#fff')};
	border-radius: 10px;
	padding: 22px 18px 15px;
	width: 264px;
	display: flex;
	flex-direction: column;
`

const PlanCardHeader = styled.div`
	display: flex;
	flex-direction: column;
	padding: 0 4px;
`

const PlanName = styled.div`
	color: #121212;
	font-size: 23px;
	font-style: normal;
	font-weight: 700;
	line-height: 34px;
`

const PlanDescription = styled.div`
	color: #4e4e4e;
	font-size: 12px;
	font-style: normal;
	font-weight: 500;
	line-height: 18px;
	height: 36px;
	white-space: pre-wrap;
	word-break: keep-all;
	margin-top: 7px;
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
`

const PlanPriceWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	margin-top: 24px;
	margin-bottom: 29px;
	gap: 6px;
`

const PlanOriginalPrice = styled.div`
	color: #a7a5a5;
	font-family: Pretendard;
	font-size: 20px;
	font-style: normal;
	font-weight: 500;
	line-height: 29.5px;
	height: 29.5px;
	text-overflow: ellipsis;
	text-decoration: line-through;
`

const PlanMonthPriceWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	gap: 3px;
`

const PlanMonthPrice = styled.div`
	color: #000;
	font-family: Pretendard;
	font-size: 30px;
	font-style: normal;
	font-weight: 500;
`

const PlanMonth = styled.div`
	color: #121212;
	font-family: Pretendard;
	font-size: 16px;
	font-style: normal;
	font-weight: 500;
	line-height: 23.5px;
`

const PlanCardDivider = styled.div`
	height: 1px;
	background: #c4c4c4;
`

const PlanFeatures = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 18.7px;
	padding: 0 4px;
	margin-top: 26px;
	min-height: 300px;
`

const PlanFeatureWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
`

const CheckIconWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;

	& svg {
		color: #7f7f7f;
	}
`

const PlanFeatureDescription = styled.div`
	color: #4e4e4e;
	font-size: 14px;
	font-style: normal;
	font-weight: 500;
	line-height: 20px;
`

const PlanCardActionButton = styled.button<{ status: string }>`
	width: 100%;
	border: ${({ status }) => (status === 'normal' ? '1px solid #A0A0A0' : 'none')};
	background: ${({ status }) => {
		let color
		if (status === 'active') {
			color = '#ffcf19'
		} else if (status === 'inactive') {
			color = '#7f7f7f'
		} else {
			color = '#fff'
		}

		return color
	}};
	padding: 12px 4px;
	cursor: ${({ status }) => (status === 'inactive' ? 'not-allowed' : 'pointer')};
	border-radius: 10px;
	color: ${({ status }) => (status === 'inactive' ? '#ffffff' : '#121212')};
	text-align: center;
	font-size: 16px;
	font-style: normal;
	font-weight: 600;
	line-height: 24px;
	margin-top: 43px;
`

const Footer = styled.div`
	display: flex;
	justify-content: space-between;
	margin-top: 28px;
`

const ModalHeaderWrapper = styled.div`
	display: flex;
	flex-direction: column;
`

const ModalHeaderTitle = styled.div`
	color: #121212;
	font-size: 18px;
	font-weight: 600;
`

const ModalHeaderSubTitle = styled.div`
	color: #4e4e4e;
	font-size: 14px;
	font-weight: 500;
`

const ModalBodyWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 24px;
`

const ModalBodyContact = styled.div`
	color: #121212;
	font-size: 14px;
	font-weight: 500;
`

const ModalBodyUrgentContact = styled.div`
	color: #910000;
	font-size: 14px;
	font-weight: 500;
`
